import React, { useState, useEffect } from 'react';
import { CommentItem } from './CommentItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, MessageCircle } from 'lucide-react';

interface Comment {
  id: string;
  sequence_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  parent_comment_id: string | null;
  is_edited: boolean;
  user_profiles: {
    user_id: string;
    full_name: string | null;
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
}

interface CommentDisplayProps {
  sequenceId: string;
}

const COMMENTS_PER_PAGE = 10;

export const CommentDisplay: React.FC<CommentDisplayProps> = ({
  sequenceId
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const loadComments = async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const from = (pageNum - 1) * COMMENTS_PER_PAGE;
      const to = from + COMMENTS_PER_PAGE - 1;

      // First, fetch the comments
      const { data: commentsData, error: fetchError, count } = await supabase
        .from('sequence_comments')
        .select('*', { count: 'exact' })
        .eq('sequence_id', sequenceId)
        .is('parent_comment_id', null) // Only top-level comments for now
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;

      if (!commentsData || commentsData.length === 0) {
        if (append) {
          setComments(prev => [...prev]);
        } else {
          setComments([]);
        }
        setHasMore(false);
        return;
      }

      // Get unique user IDs from comments
      const userIds = [...new Set(commentsData.map(comment => comment.user_id))];

      // Fetch user profiles for these users
      const { data: profilesData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, display_name, username, avatar_url')
        .in('user_id', userIds);

      if (profileError) throw profileError;

      // Create a map of user profiles for quick lookup
      const profilesMap = new Map(
        (profilesData || []).map(profile => [profile.user_id, profile])
      );

      // Combine comments with user profiles
      const commentsWithProfiles: Comment[] = commentsData.map(comment => ({
        ...comment,
        user_profiles: profilesMap.get(comment.user_id) || {
          user_id: comment.user_id,
          full_name: null,
          display_name: null,
          username: null,
          avatar_url: null
        }
      }));

      if (append) {
        setComments(prev => [...prev, ...commentsWithProfiles]);
      } else {
        setComments(commentsWithProfiles);
      }

      // Check if there are more comments to load
      const totalLoaded = append ? comments.length + commentsWithProfiles.length : commentsWithProfiles.length;
      setHasMore((count || 0) > totalLoaded);

    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreComments = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadComments(nextPage, true);
  };

  const handleCommentUpdate = () => {
    // Refresh comments when a comment is updated or deleted
    setPage(1);
    loadComments(1, false);
  };

  useEffect(() => {
    loadComments();
  }, [sequenceId]);

  // Set up real-time subscription for new comments
  useEffect(() => {
    const subscription = supabase
      .channel(`sequence_comments_${sequenceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sequence_comments',
          filter: `sequence_id=eq.${sequenceId}`
        },
        (payload) => {
          // Refresh comments when a new comment is added
          loadComments(1, false);
          setPage(1);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [sequenceId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center space-x-2 py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading comments...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => loadComments()} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (comments.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No comments yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          onUpdate={handleCommentUpdate}
        />
      ))}

      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={loadMoreComments}
            disabled={loadingMore}
            variant="outline"
          >
            {loadingMore ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Loading more...</span>
              </div>
            ) : (
              'Load More Comments'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentDisplay;